// src/utils/merkleAuth.js
// Merkeziyetsiz 2FA sistemi - Merkle Ağacı tabanlı kimlik doğrulama
import { 
  keccak256, 
  toUtf8Bytes, 
  concat, 
  hexlify, 
  getBytes,
  randomBytes
} from 'ethers';
import CryptoJS from 'crypto-js';

// Base64 kodlama/çözme yardımcı fonksiyonları
const _byteArrayToBase64 = (bytes) => {
  return btoa(String.fromCharCode.apply(null, bytes));
};

const _base64ToByteArray = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Merkeziyetsiz Çift Faktörlü Kimlik Doğrulama Sistemi
 * Bu sistem kullanıcının cihazında üretilen tek kullanımlık şifreleri (TOTP)
 * Merkle ağacı tabanlı doğrulama ile birleştirerek güvenli ve merkeziyetsiz bir
 * 2FA çözümü sunar.
 */
class MerkleAuth {
  constructor() {
    this.timeWindow = 30; // TOTP zaman penceresi (saniye)
    this.authCounter = 0; // Doğrulama sayacı
    this.secretKey = null; // Gizli anahtar
    this.recoveryKey = null; // Kurtarma anahtarı
    this.backupCodes = []; // Yedek kodlar
    this.merkleTreeDepth = 10; // Merkle ağacı derinliği
  }

  /**
   * Yeni bir 2FA kurulumu başlatır
   * @param {string} walletAddress - Kullanıcının cüzdan adresi
   * @returns {Object} Kurulum bilgileri
   */
  async initializeSetup(walletAddress) {
    try {
      // Kullanıcının cüzdan adresi ve rastgele değerlerle gizli anahtar oluştur
      const randomData = randomBytes(32);
      const timestamp = Date.now();
      const userSpecificData = keccak256(
        concat([
          getBytes(walletAddress),
          getBytes(hexlify(timestamp)),
          randomData
        ])
      );
      
      // Gizli anahtarı oluştur ve base32 formatına dönüştür
      this.secretKey = _byteArrayToBase64(getBytes(userSpecificData)).substring(0, 16);
      const secretKeyBase32 = this._toBase32(this.secretKey);
      
      // Kurtarma anahtarı oluştur
      this.recoveryKey = this._generateRecoveryKey(24);
      
      // QR kod için gerekli URI'yi oluştur
      const otpAuthUrl = `otpauth://totp/IDapps:${walletAddress.substring(0, 8)}...?secret=${secretKeyBase32}&issuer=IDapps&algorithm=SHA1&digits=6&period=${this.timeWindow}`;
      
      // Yedek kodlar oluştur
      this.backupCodes = this._generateBackupCodes(10);
      
      return {
        secretKey: this.secretKey,
        secretKeyBase32,
        otpAuthUrl,
        recoveryKey: this.recoveryKey,
        backupCodes: this.backupCodes,
        setupTime: timestamp
      };
    } catch (error) {
      console.error('2FA kurulum hatası:', error);
      throw new Error('2FA kurulumu sırasında bir hata oluştu');
    }
  }

  /**
   * Belirli bir zamanda geçerli TOTP kodunu oluşturur
   * @param {number} counter Zaman dilimi sayacı (varsayılan olarak şu anki zaman)
   * @returns {string} 6 haneli TOTP kodu
   */
  generateTOTP(counter = Math.floor(Date.now() / 1000 / this.timeWindow)) {
    if (!this.secretKey) {
      throw new Error('2FA henüz kurulmadı. Önce initializeSetup() çağırın.');
    }

    try {
      // HMAC-SHA1 ile kod oluştur
      const data = this._intToBytes(counter);
      const key = CryptoJS.enc.Base64.parse(this.secretKey);
      const message = CryptoJS.lib.WordArray.create(data);
      const hmacResult = CryptoJS.HmacSHA1(message, key);
      
      // HMAC sonucundan 6 haneli kod oluştur
      const hmacHex = hmacResult.toString(CryptoJS.enc.Hex);
      const offset = parseInt(hmacHex.substring(hmacHex.length - 1), 16);
      const binary = parseInt(hmacHex.substring(offset * 2, offset * 2 + 8), 16) & 0x7fffffff;
      
      return (binary % 1000000).toString().padStart(6, '0');
    } catch (error) {
      console.error('TOTP oluşturma hatası:', error);
      return '000000'; // Hata durumunda geçersiz kod
    }
  }

  /**
   * Mevcut zamanla eşleşen TOTP kodunu oluşturur
   * @returns {string} Geçerli 6 haneli TOTP kodu
   */
  getCurrentTOTP() {
    const counter = Math.floor(Date.now() / 1000 / this.timeWindow);
    return this.generateTOTP(counter);
  }

  /**
   * Kullanıcı tarafından girilen TOTP kodunu doğrular
   * @param {string} userInputCode Kullanıcının girdiği kod
   * @returns {boolean} Kod geçerliyse true
   */
  verifyTOTP(userInputCode) {
    if (!this.secretKey) {
      throw new Error('2FA henüz kurulmadı. Önce initializeSetup() çağırın.');
    }
    
    try {
      const currentCounter = Math.floor(Date.now() / 1000 / this.timeWindow);
      
      // Şimdiki, bir önceki ve bir sonraki zaman dilimindeki kodları kontrol et
      // Bu, saat senkronizasyon sorunlarını önler
      for (let i = -1; i <= 1; i++) {
        const counter = currentCounter + i;
        const validCode = this.generateTOTP(counter);
        
        if (validCode === userInputCode) {
          this.authCounter++; // Başarılı doğrulamaları say
          return true;
        }
      }
      
      // Yedek kodları da kontrol et
      if (this.backupCodes && this.backupCodes.includes(userInputCode)) {
        // Kullanılmış yedek kodu listeden çıkar (tek kullanımlık)
        this.backupCodes = this.backupCodes.filter(code => code !== userInputCode);
        this.authCounter++;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('TOTP doğrulama hatası:', error);
      return false;
    }
  }

  /**
   * Doğrulama bilgilerini blockchain'e kaydedilecek şekilde hazırlar
   * @returns {Object} Merkle kökü ve kanıt
   */
  generateAuthProof() {
    if (!this.secretKey) {
      throw new Error('2FA henüz kurulmadı. Önce initializeSetup() çağırın.');
    }
    
    try {
      // Son N TOTP'yi içeren bir Merkle ağacı oluştur
      const currentCounter = Math.floor(Date.now() / 1000 / this.timeWindow);
      const leaves = [];
      
      // Gelecekteki bir dizi kodu içeren yapraklar oluştur
      for (let i = 0; i < this.merkleTreeDepth; i++) {
        const counter = currentCounter + i;
        const code = this.generateTOTP(counter);
        // Doğrudan kodu değil, hash'ini kullan
        const leaf = keccak256(toUtf8Bytes(`${this.secretKey}-${code}-${counter}`));
        leaves.push(leaf);
      }
      
      // Merkle ağacını oluştur
      const merkleTree = this._buildMerkleTree(leaves);
      const merkleRoot = merkleTree[merkleTree.length - 1][0];
      
      // Geçerli kodun kanıtını oluştur
      const currentCode = this.generateTOTP(currentCounter);
      const leaf = keccak256(toUtf8Bytes(`${this.secretKey}-${currentCode}-${currentCounter}`));
      const proof = this._generateMerkleProof(leaves, 0, merkleTree);
      
      return {
        merkleRoot,
        currentLeaf: leaf,
        proof,
        timestamp: Date.now(),
        authCounter: this.authCounter
      };
    } catch (error) {
      console.error('Doğrulama kanıtı oluşturma hatası:', error);
      return null;
    }
  }

  /**
   * Merkle Ağacı oluşturur
   * @private
   * @param {Array} leaves - Ağacın yaprak düğümleri
   * @returns {Array} Merkle ağacı (katmanlar halinde)
   */
  _buildMerkleTree(leaves) {
    if (leaves.length === 0) return [['0x0000000000000000000000000000000000000000000000000000000000000000']];
    
    const tree = [leaves];
    let layer = leaves;
    
    // Tek bir kök düğüm kalana kadar ağacı oluştur
    while (layer.length > 1) {
      const newLayer = [];
      
      for (let i = 0; i < layer.length; i += 2) {
        if (i + 1 < layer.length) {
          // İki yaprak düğümü sırala ve birleştir
          const a = layer[i].toLowerCase() < layer[i + 1].toLowerCase() ? layer[i] : layer[i + 1];
          const b = layer[i].toLowerCase() < layer[i + 1].toLowerCase() ? layer[i + 1] : layer[i];
          newLayer.push(keccak256(concat([getBytes(a), getBytes(b)])));
        } else {
          // Tek kalan yaprağı yukarı taşı
          newLayer.push(layer[i]);
        }
      }
      
      tree.push(newLayer);
      layer = newLayer;
    }
    
    return tree;
  }

  /**
   * Merkle Kanıtı oluşturur
   * @private
   * @param {Array} leaves - Ağacın yaprak düğümleri
   * @param {number} index - Kanıt oluşturulacak yaprak indeksi
   * @param {Array} tree - Merkle ağacı (opsiyonel, verilmezse yeniden oluşturulur)
   * @returns {Array} Merkle kanıtı
   */
  _generateMerkleProof(leaves, index, tree = null) {
    if (!tree) {
      tree = this._buildMerkleTree(leaves);
    }
    
    const proof = [];
    let idx = index;
    
    // Her katman için kanıtı oluştur
    for (let i = 0; i < tree.length - 1; i++) {
      const layer = tree[i];
      const isRightNode = idx % 2 === 0;
      const siblingIdx = isRightNode ? idx + 1 : idx - 1;
      
      // Kardeş düğüm varsa, kanıta ekle
      if (siblingIdx < layer.length) {
        proof.push({
          data: layer[siblingIdx],
          position: isRightNode ? 'right' : 'left'
        });
      }
      
      // Üst katmandaki indeksi hesapla
      idx = Math.floor(idx / 2);
    }
    
    return proof;
  }

  /**
   * 10 adet yedek kod oluşturur
   * @private
   * @param {number} count - Oluşturulacak kod sayısı
   * @returns {Array} Yedek kodlar
   */
  _generateBackupCodes(count = 10) {
    const codes = [];
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    for (let i = 0; i < count; i++) {
      let code = '';
      for (let j = 0; j < 8; j++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      codes.push(code);
    }
    
    return codes;
  }

  /**
   * Kurtarma anahtarı oluşturur
   * @private
   * @param {number} length - Anahtar uzunluğu
   * @returns {string} Kurtarma anahtarı
   */
  _generateRecoveryKey(length = 24) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let key = '';
    
    // Her 4 karakterde bir tire ekleyen bir kurtarma anahtarı oluştur
    for (let i = 0; i < length; i++) {
      if (i > 0 && i % 4 === 0) key += '-';
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return key;
  }

  /**
   * Lokal olarak saklanan 2FA bilgilerini şifreli olarak dışa aktarır
   * @param {string} password Şifreleme için parola
   * @returns {string} Şifrelenmiş 2FA verileri
   */
  exportEncryptedData(password) {
    if (!this.secretKey) {
      throw new Error('2FA henüz kurulmadı. Önce initializeSetup() çağırın.');
    }
    
    try {
      const data = {
        secretKey: this.secretKey,
        recoveryKey: this.recoveryKey,
        backupCodes: this.backupCodes,
        authCounter: this.authCounter,
        timeWindow: this.timeWindow,
        merkleTreeDepth: this.merkleTreeDepth,
        version: '1.0.0',
        timestamp: Date.now()
      };
      
      // AES şifreleme kullan
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        password
      ).toString();
      
      return encryptedData;
    } catch (error) {
      console.error('Veri dışa aktarma hatası:', error);
      return null;
    }
  }

  /**
   * Dışa aktarılmış 2FA verilerini içe aktarır
   * @param {string} encryptedData Şifrelenmiş veri
   * @param {string} password Şifre çözme için parola
   * @returns {boolean} İçe aktarma başarılıysa true
   */
  importEncryptedData(encryptedData, password) {
    try {
      // Veriyi çöz
      const decryptedData = CryptoJS.AES.decrypt(encryptedData, password).toString(CryptoJS.enc.Utf8);
      const data = JSON.parse(decryptedData);
      
      // Verileri doğrula ve yükle
      if (data.secretKey) {
        this.secretKey = data.secretKey;
        this.recoveryKey = data.recoveryKey || null;
        this.backupCodes = data.backupCodes || [];
        this.authCounter = data.authCounter || 0;
        this.timeWindow = data.timeWindow || 30;
        this.merkleTreeDepth = data.merkleTreeDepth || 10;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Veri içe aktarma hatası:', error);
      return false;
    }
  }

  /**
   * Base32 kodlaması yapar (QR kod için)
   * @private
   */
  _toBase32(str) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let bits = '';
    let result = '';
    
    // String'i bit dizisine dönüştür
    for (let i = 0; i < str.length; i++) {
      const bin = str.charCodeAt(i).toString(2).padStart(8, '0');
      bits += bin;
    }
    
    // 5 bitlik gruplara böl ve base32 karakterlerine dönüştür
    for (let i = 0; i + 5 <= bits.length; i += 5) {
      const chunk = bits.slice(i, i + 5);
      result += chars[parseInt(chunk, 2)];
    }
    
    return result;
  }

  /**
   * Sayıyı byte dizisine dönüştürür
   * @private
   */
  _intToBytes(num) {
    const bytes = new Uint8Array(8);
    for (let i = 7; i >= 0; i--) {
      bytes[i] = num & 0xff;
      num = num >> 8;
    }
    return bytes;
  }
}

// Sınıfın dışa aktarımı
export default MerkleAuth;